import {TableRow} from 'platform/components';
import {Box, Text, VStack} from 'platform/foundation';

import {
  FinancingRateMatrixCell,
  SaveSaleVehicleFinancingBody,
  VehicleFinancingType,
} from '@dms/api/vehicle';
import {RATE_MATRIX_DOWN_PAYMENTS} from '@dms/features/vehicle-financing-template';
import i18n from '@dms/i18n';

import {FinancingMatrixRowCell} from './FinancingMatrixRowCell';
import {FinancingCellCoordinates} from './types/FinancingCellCoordinates';
import {VehicleFinancingPricing} from './types/VehicleFinancingPricing';
import {getFinancingCellKey} from './utils/financingCells/getFinancingCellKey';

interface FinancingMatrixRowProps {
  vehicleFinancingBody: SaveSaleVehicleFinancingBody;
  type: VehicleFinancingType;
  term: number;
  balloonPayment: number | null;
  cellsByCoordinateKey: Map<string, FinancingRateMatrixCell>;
  templateCellsByCoordinateKey: Map<string, FinancingRateMatrixCell>;
  selectedCellKeys: Set<string>;
  vehiclePrice: number;
  currency: string;
  pricing: VehicleFinancingPricing | null;
  isTemplatePending?: boolean;
  isReadOnly: boolean;
  onToggleSelection: (coordinates: FinancingCellCoordinates) => void;
  onEditCell: (cell: FinancingCellCoordinates) => void;
}

/** One term of the financing rate matrix: the term label followed by its down-payment cells. */
export function FinancingMatrixRow(props: FinancingMatrixRowProps) {
  return (
    <TableRow hoverColor="general.transparent">
      <Box paddingHorizontal={1} paddingTop={1}>
        <VStack height={15} justify="center" align="center">
          <Text size="xSmall" color="secondary">
            {i18n.t('page.vehicle.financingAndLeasing.monthsValue', {term: props.term})}
          </Text>
        </VStack>
      </Box>
      {RATE_MATRIX_DOWN_PAYMENTS.map((downPayment) => {
        const cellKey = getFinancingCellKey({
          term: props.term,
          downPayment,
          balloonPayment: props.balloonPayment,
        });
        const selectionKey = getFinancingCellKey({
          term: props.term,
          downPayment,
          balloonPayment: props.type === 'BALLOON' ? props.balloonPayment : null,
        });

        return (
          <FinancingMatrixRowCell
            key={downPayment}
            vehicleFinancingBody={props.vehicleFinancingBody}
            type={props.type}
            term={props.term}
            downPayment={downPayment}
            balloonPayment={props.balloonPayment}
            cell={props.cellsByCoordinateKey.get(cellKey) ?? null}
            templateCell={props.templateCellsByCoordinateKey.get(cellKey)}
            vehiclePrice={props.vehiclePrice}
            currency={props.currency}
            pricing={props.pricing}
            isTemplatePending={props.isTemplatePending}
            isReadOnly={props.isReadOnly}
            isSelected={props.selectedCellKeys.has(selectionKey)}
            onToggleSelection={props.onToggleSelection}
            onEditCell={props.onEditCell}
          />
        );
      })}
    </TableRow>
  );
}
